import { RefObject, useEffect } from 'react'

// iOS Safari ignores `overflow: hidden` on body; pinning with `position: fixed` + restoring scrollY is the only reliable lock. Ref-counted so nested modals don't fight each other.
// Exception: on iOS Safari, `position: fixed` resets the browser's internal scroll-position tracking,
// causing the native Smart App Banner to reappear when a drawer opens. Use overflow:hidden on <html> instead (reliable on iOS 16+).
let lockCount = 0
let savedScrollY = 0
let savedBodyStyles: {
  position: string
  top: string
  left: string
  right: string
  width: string
} | null = null

// Evaluated once at module load (server-side navigator is undefined → false, which is correct).
const isIOSSafari =
  typeof navigator !== 'undefined' &&
  /iP(hone|od|ad)/.test(navigator.userAgent) &&
  !/CriOS|FxiOS/.test(navigator.userAgent)

function lockBody() {
  const body = document.body
  body.classList.add('overflowHidden')
  body.classList.remove('overflowUnset')

  if (lockCount === 0) {
    savedScrollY = window.scrollY
    savedBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    }

    if (isIOSSafari) {
      // position:fixed on body resets Safari's scroll-position tracking → native Smart App Banner
      // reappears (Safari interprets it as "scrolled to top"). Locking overflow on the root
      // element avoids that side-effect while still preventing body scroll on iOS 16+.
      document.documentElement.style.overflow = 'hidden' // eslint-disable-line i18next/no-literal-string
    } else {
      body.style.position = 'fixed'
      body.style.top = `-${savedScrollY}px`
      body.style.left = '0'
      body.style.right = '0'
      body.style.width = '100%'
    }
  }
  lockCount += 1
}

function unlockBody() {
  const body = document.body
  body.classList.add('overflowUnset')
  body.classList.remove('overflowHidden')

  lockCount = Math.max(0, lockCount - 1)
  if (lockCount > 0) return

  if (savedBodyStyles) {
    if (isIOSSafari) {
      document.documentElement.style.overflow = ''
    } else {
      body.style.position = savedBodyStyles.position
      body.style.top = savedBodyStyles.top
      body.style.left = savedBodyStyles.left
      body.style.right = savedBodyStyles.right
      body.style.width = savedBodyStyles.width
      window.scrollTo(0, savedScrollY)
    }
    savedBodyStyles = null
  }
}

const useLockBodyScroll = (freeze: boolean, contentRef?: RefObject<HTMLElement>) => {
  useEffect(() => {
    if (!freeze) return

    lockBody()

    let preventScrollOutsideModal: ((e: TouchEvent) => void) | undefined
    if (contentRef) {
      preventScrollOutsideModal = (e: TouchEvent) => {
        if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
          e.preventDefault()
        }
      }
      document.addEventListener('touchmove', preventScrollOutsideModal, { passive: false })
    }

    return () => {
      unlockBody()
      if (preventScrollOutsideModal) {
        document.removeEventListener('touchmove', preventScrollOutsideModal)
      }
    }
  }, [freeze, contentRef])
}

export default useLockBodyScroll
